import { Eye, Users, MousePointerClick, RefreshCw, BarChart3 } from "lucide-react";
import { resolveImageUrl, PLACEHOLDER } from "@/utils/imageUrl";

const Metric = ({ icon: Icon, value, label, color, testId }) => (
  <div className="glass-card p-5 text-center" data-testid={testId}>
    <Icon className={`w-5 h-5 ${color} mx-auto mb-2`} />
    <p className={`text-2xl font-oswald font-bold ${color === "text-cyan-400" ? "text-white" : color}`}>{value}</p>
    <p className="text-xs text-[#A0A0A0] uppercase tracking-wider mt-1">{label}</p>
  </div>
);

const Breakdown = ({ breakdown }) => (
  <div className="glass-card p-5" data-testid="action-breakdown">
    <h3 className="font-oswald text-lg font-bold text-white mb-4 uppercase">Tipo de Interaccion</h3>
    <div className="grid grid-cols-3 gap-4">
      {[["view", "Vistas", "text-cyan-400"], ["whatsapp", "WhatsApp", "text-[#25D366]"], ["payment", "Pagos", "text-[#B026FF]"]].map(([key, label, color]) => (
        <div key={key} className="bg-[#050505] rounded-xl p-4 text-center border border-white/5">
          <p className={`font-oswald text-2xl font-bold ${color}`}>{breakdown[key] || 0}</p>
          <p className="text-xs text-[#A0A0A0] mt-1">{label}</p>
        </div>
      ))}
    </div>
  </div>
);

const DailyChart = ({ dailyViews }) => {
  const entries = Object.entries(dailyViews);
  const maxVal = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="glass-card p-5" data-testid="daily-views-chart">
      <h3 className="font-oswald text-lg font-bold text-white mb-4 uppercase">Visitas Ultimos 30 Dias</h3>
      <div className="flex items-end gap-1 h-32 overflow-x-auto pb-1">
        {entries.map(([date, count]) => (
          <div key={date} className="flex flex-col items-center gap-1 min-w-[24px] flex-1" title={`${date}: ${count} visitas`}>
            <span className="text-[10px] text-[#A0A0A0]">{count}</span>
            <div className="w-full rounded-t-md bg-gradient-to-t from-cyan-500 to-cyan-400 min-h-[4px]" style={{ height: `${(count / maxVal) * 100}%` }} />
            <span className="text-[8px] text-[#A0A0A0] rotate-[-45deg] whitespace-nowrap">{date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TopProducts = ({ products, onRefresh }) => (
  <div className="glass-card overflow-hidden" data-testid="top-products">
    <div className="p-4 border-b border-white/5 flex items-center justify-between">
      <h3 className="font-oswald text-lg font-bold text-white uppercase">Productos Mas Vistos</h3>
      <button onClick={onRefresh} className="text-[#A0A0A0] hover:text-cyan-400 transition-colors" data-testid="refresh-analytics-btn"><RefreshCw className="w-4 h-4" /></button>
    </div>
    <div className="divide-y divide-white/5">
      {products.map((tp, idx) => (
        <div key={tp.product_id} className="flex items-center gap-3 p-3" data-testid={`top-product-${idx}`}>
          <span className="text-xs text-[#A0A0A0] w-5 text-right font-mono">{idx + 1}</span>
          <img src={resolveImageUrl(tp.image_url) || PLACEHOLDER} alt={tp.name} className="w-10 h-10 rounded-lg object-cover bg-[#050505]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{tp.name}</p>
            {tp.brand && <p className="text-xs text-[#B026FF]">{tp.brand}</p>}
          </div>
          <div className="text-right">
            <p className="text-sm font-oswald font-bold text-cyan-400">{tp.clicks}</p>
            <p className="text-[10px] text-[#A0A0A0]">clicks</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState = ({ message, hint, onRetry }) => (
  <div className="text-center py-20 glass-card">
    <BarChart3 className="w-10 h-10 text-[#A0A0A0] mx-auto mb-3" />
    <p className="text-[#A0A0A0]">{message}</p>
    {hint && <p className="text-xs text-[#A0A0A0]/60 mt-1">{hint}</p>}
    {onRetry && <button onClick={onRetry} className="text-cyan-400 text-sm hover:underline mt-2">Reintentar</button>}
  </div>
);

export const AnalyticsPanel = ({ data, loading, onRefresh }) => {
  if (loading) {
    return <div className="flex justify-center py-20" data-testid="analytics-section"><div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!data) {
    return <div data-testid="analytics-section"><EmptyState message="Error cargando datos" onRetry={onRefresh} /></div>;
  }
  const hasBreakdown = data.action_breakdown && Object.keys(data.action_breakdown).length > 0;
  const hasDaily = data.daily_views && Object.keys(data.daily_views).length > 0;
  const isEmpty = data.total_views === 0 && data.total_clicks === 0;

  return (
    <div className="space-y-6" data-testid="analytics-section">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Metric icon={Eye} value={data.total_views} label="Visitas Totales" color="text-cyan-400" testId="metric-total-views" />
        <Metric icon={Users} value={data.today_views} label="Visitas Hoy" color="text-[#00FF66]" testId="metric-today-views" />
        <Metric icon={MousePointerClick} value={data.total_clicks} label="Clicks Totales" color="text-[#B026FF]" testId="metric-total-clicks" />
        <Metric icon={Users} value={data.unique_visitors_today} label="Unicos Hoy" color="text-amber-400" testId="metric-unique-visitors" />
      </div>
      {hasBreakdown && <Breakdown breakdown={data.action_breakdown} />}
      {hasDaily && <DailyChart dailyViews={data.daily_views} />}
      {data.top_products?.length > 0 && <TopProducts products={data.top_products} onRefresh={onRefresh} />}
      {isEmpty && <EmptyState message="No hay datos de trafico aun" hint="Los datos aparecen cuando los clientes visitan tu tienda" />}
    </div>
  );
};
